import { AfterViewInit, ChangeDetectorRef, Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';

import { ValidationHistoryFacade } from '@ridango/components';
import { AppStartupService, AuthService, TimeUtil } from '@ridango/core';
import Env from '@ridango/core/env';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'rwidget-validation-history',
    templateUrl: './validation-history.component.html',
    styleUrls: ['./validation-history.component.scss']
})
export class ValidationHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
    private componentDestroyed$ = new Subject<void>();

    validations$ = this.historyFacade.validations$;
    loading$ = this.historyFacade.loading$;
    startDate$ = this.historyFacade.startDate$;
    endDate$ = this.historyFacade.endDate$;
    minStartDate$ = this.historyFacade.minStartDate$;
    minEndDate$ = this.historyFacade.minEndDate$;
    maxStartDate$ = this.historyFacade.maxStartDate$;
    maxEndDate$ = this.historyFacade.maxEndDate$;
    diff$ = this.historyFacade.diff$;

    startDate: Date;
    endDate: Date;
    token = this.ridangoAuth.getLocalAccessToken();
    dateFormat = this.env.PRIMARY_DATE_FORMAT;

    validationTableColumns: string[] = this.env.VALIDATION_TABLE_COLUMNS;

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(LOCALE_ID) public locale: string,
        private historyFacade: ValidationHistoryFacade,
        public translate: TranslateService,
        public appStartupService: AppStartupService,
        private ridangoAuth: AuthService,
        private cdr: ChangeDetectorRef,
        private http: HttpClient
    ) {}

    ngOnInit() {
        this.locale = this.translate.currentLang;

        this.historyFacade.startDate$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((startDate) => (this.startDate = startDate));

        this.historyFacade.endDate$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((endDate) => (this.endDate = endDate));
    }

    ngAfterViewInit() {
        this.historyFacade.fetchValidationHistory(this.startDate, this.endDate, this.token);
        this.cdr.detectChanges();
    }

    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    startUpdated(date: Date) {
        this.historyFacade.setStartDate(date);
    }

    endUpdated(date: Date) {
        this.historyFacade.setEndDate(date);
    }

    updateList() {
        this.historyFacade.fetchValidationHistory(this.startDate, this.endDate, this.token);
    }

    async downloadValidationHistoryPdf() {
        if (!this.token) {
            console.error('No authentication token available');
            return;
        }

        const regionId = this.env.REGION_ID;

        if (!this.startDate || !this.endDate) {
            console.error('Start date and end date are required');
            return;
        }

        const baseUrl = this.env.CORE_API_URL?.replace('/core', '') || '';
        const url = `${baseUrl}/v2/${regionId}/cart/pdf/validation-history`;

        const requestBody = {
            jwt_token: this.token,
            region_id: regionId,
            lang: this.translate.currentLang || 'et',
            startDate: TimeUtil.formatDateToISO(this.startDate),
            endDate: TimeUtil.formatDateToISO(this.endDate)
        };

        const headers = new HttpHeaders({
            'Accept': 'application/pdf',
            'Content-Type': 'application/json'
        });

        try {
            const blob = await this.http.put(url, requestBody, { headers, responseType: 'blob' }).toPromise();
            saveAs(blob, 'validation-history.pdf');
        } catch (error) {
            console.error('Error downloading validation history PDF:', error);
        }
    }
}
